import {
	TOS_ACCEPTED,
	USER_CREATED,
	USER_LOGIN_SUCCESSFUL,
	USER_LOGOUT,
	USER_SUSPEND_UPDATED,
	USER_UPDATED,
	USERS_FETCHED
} from "./actionType";
import {Dispatch} from "redux";
import {api} from "../../../api/api";
import {Action} from "redux-actions";
import User from "../../../model/user/User";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import CookieHelper from "../../../helper/CookieHelper";
import UserData from "../../../model/user/UserData";
import {RequestResetPasswordApiDto} from "../../../api/model/rest";
import {fetchAcquisitionChannels} from "../acquisitionChannel/acquisitionChannel";
import UserConsentData from "../../../model/user/UserConsentData";
import {toast} from "react-toastify";
import {fetchLeadTypes} from "../leadType/leadType";
import {fetchAppointmentTypes, fetchJobAppointmentTypes} from "../appointment/appointment";
import {fetchBrands} from "../brand/brand";
import {fetchJobTags, fetchTags} from "../tag/tags";
import LocalStorage from "../../local/LocalStorage";
import {none, Option, option} from "ts-option";
import {fetchOrganization} from "../organization/organization";
import {fetchTasks} from "../task/task";
import {fetchTextMessages} from "../sms/sms";
import {fetchSettings} from "../settings/settings";
import {fetchJobTypes} from "../jobType/jobType";
import {fetchCategories} from "../category/category";
import {fetchJobStatuses, jobWarrantyRegistrationStatuses} from "../jobStatus/jobStatus";
import {fetchLeadStatusConfig, fetchLeadStatuses} from "../leadStatus/leadStatus";
import {fetchAssetCategories} from "../assetCategory/assetCategory";
import {fetchMerchants} from "../merchant/merchant";
import {fetchJobCustomFields, fetchLeadCustomFields, fetchPartnerBrandCustomFields} from "../customField/customField";
import {fetchJobSidebarOrder, fetchLeadSidebarOrder} from "../sidebar/sidebar";
import {fetchFinanceProducts} from "../financeProduct/financeProduct";
import {notifyBugsnag} from "../../../utils/Utils";

const mixpanel = require("mixpanel-browser");

/**
 * Login successful action.
 * @param {string} user User model.
 * @returns {Action<USER_LOGIN_SUCCESSFUL>}
 */
export function loginSuccess(user: User): Action<USER_LOGIN_SUCCESSFUL> {
	mixpanel.identify(user.id);
	mixpanel.people.set({email: user.data.username, first_name: user.data.firstName, last_name: user.data.lastName});
	LocalStorage.user = option(user);
	return {type: USER_LOGIN_SUCCESSFUL, payload: {user}};
}

/**
 * User logout action.
 * @returns {Action<USER_LOGOUT>}
 */
export function userLogOut(): Action<USER_LOGOUT> {
	mixpanel.reset();
	LocalStorage.user = none;
	return {type: USER_LOGOUT, payload: {}};
}

/**
 * Users fetched action.
 * @param {User[]} users Organisation users.
 * @returns {Action<USERS_FETCHED>}
 */
export function usersFetched(users: User[]): Action<USERS_FETCHED> {
	return {type: USERS_FETCHED, payload: {users}};
}

/**
 * User created action.
 * @param {User} user Created user.
 * @return {Action<USER_CREATED>}
 */
export function userCreated(user: User): Action<USER_CREATED> {
	return {type: USER_CREATED, payload: {user}}
}

/**
 * User updated action.
 * @param {User} user Updated user.
 * @return {Action<USER_CREATED>}
 */

export function userUpdated(user: User): Action<USER_UPDATED> {
	return {type: USER_UPDATED, payload: {user}}
}

/**
 * Accept terms of service action
 * @returns {Action<TOS_ACCEPTED>}
 */
export function tosAccepted(): Action<TOS_ACCEPTED> {
	return {type: TOS_ACCEPTED, payload: {}}
}

export function userSuspendUpdated(userId: string, disabled: boolean): Action<USER_SUSPEND_UPDATED> {
	return {type: USER_SUSPEND_UPDATED, payload: {userId, disabled}}
}

/**
 * Login action.
 */
export function logIn(userName: string, password: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.userApi.loginUser(userName, password).then(() => {
			dispatch(checkUserData(onSuccess));
			toast.dismiss();
		}).catch(onFailure);
	}
}

/**
 * Check session user data action.
 */
export function checkUserData(onSuccess: OnSuccess) {
	return function(dispatch: Dispatch<any>) {
		api.instance.userApi.getUser().then((user: User) => {
			onSuccess();
			dispatch(loginSuccess(user));
			if (user.canFetchData())
				initializeData(dispatch);
		}).catch(() => {
			dispatch(userLogOut());//TODO: Create action corresponding to not logged in user not logout.
		})
	}
}

/**
 * User logout action.
 */
export function logOut() {
	return function(dispatch: Dispatch<any>) {
		api.instance.userApi.logoutUser()
			.catch((error) => console.log("Error while logging out: " + JSON.stringify(error)));
		CookieHelper.cleaCookies();
		dispatch(userLogOut());
		toast.dismiss();
	}
}

/**
 * Fetch users action.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchUsers(onSuccess: OnSuccess, onFailure: OnFailure) {
	return (dispatch: Dispatch<any>) => {
		api.instance.userApi.getUsers().then((users) => {
			onSuccess();
			dispatch(usersFetched(users));
		}).catch(onFailure);
	};
}

/**
 * Create user action.
 * @param {UserData} data User data object.
 * @param {string} password user password.
 * @param {Option<File>} file User photo.
 * @param {OnSuccessData<User>} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function createUser(data: UserData,
	password: string,
	file: Option<File>,
	onSuccess: OnSuccessData<User>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const create = (userData: UserData) => {
			const user = User.toUserCreateJson(password, userData);
			api.instance.userApi.createUser(user).then((user: User) => {
				dispatch(userCreated(user));
				onSuccess(user);

			}).catch(onFailure)
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				const newUserData = data.copy({photoId: option(id)});
				return create(newUserData);
			})
		}
		else {
			return create(data);
		}
	}
}

/**
 * Update user action
 * @param {string} userId User identifier
 * @param {UserData} data User data object.
 * @param {Option<File>} file User photo.
 * @param shouldRemoveFileOnUpdate should remove file on update indicator
 * @param {OnSuccessData<User>} onSuccess Success callback
 * @param {OnFailure} onFailure Failure callback.
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function updateUser(userId: string,
	data: UserData,
	file: Option<File>,
	shouldRemoveFileOnUpdate: boolean,
	onSuccess: OnSuccessData<User>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const update = (userData: UserData) => {
			const user = User.toUserUpdateJson(userData);
			api.instance.userApi.updateUser(userId, user).then((user: User) => {
				dispatch(userUpdated(user));
				onSuccess(user)
			}).catch(onFailure)
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				const newUserData = data.copy({photoId: option(id)});
				return update(newUserData);
			})
		}
		else {
			const newUserData = data.copy({photoId: shouldRemoveFileOnUpdate ? none : data.photoId});
			return update(newUserData)
		}
	};
}

/**
 * Set user password.
 * @param {string} password User password.
 * @param {string} token Password change token form API.
 * @param {OnSuccess} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback
 * @return {() => void}
 */
export function setPassword(password: string, token: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function() {
		api.instance.userApi.setPassword(password, token).then(onSuccess).catch(onFailure);
	}
}

/**
 * Request reset password action.
 * @param {string} email user email.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {() => void}
 */
export function requestResetPassword(email: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function() {
		const data: RequestResetPasswordApiDto = {email};
		api.instance.userApi.requestResetPassword(data).then(onSuccess).catch(onFailure);
	}
}

/**
 * Accept terms of service
 * @param {UserConsentData} data User consent data.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function acceptToS(data: UserConsentData, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.userApi.acceptTos(data).then(() => {
				onSuccess();
				dispatch(tosAccepted());
				initializeData(dispatch);
			}
		).catch(onFailure);
	}
}

/**
 * Disabled user action
 * @param {string} userId
 * @param {boolean} disabled
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: Dispatch<any>) => void}
 */
export function changeUserSuspend(userId: string, disabled: boolean, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.userApi.changeSuspend(userId, disabled).then(() => {
			dispatch(userSuspendUpdated(userId, disabled));
			onSuccess()
		}).catch(onFailure)
	}
}

/**
 * Initializes data after user signs in.
 * @param {Dispatch<any>} dispatch dispatch method.
 */
function initializeData(dispatch: Dispatch<any>) {
	dispatch(fetchSettings(() => {}, notifyBugsnag));
	dispatch(fetchJobSidebarOrder(() => {}, notifyBugsnag))
	dispatch(fetchLeadSidebarOrder(() => {}, notifyBugsnag));
	dispatch(fetchUsers(() => {}, notifyBugsnag));
	dispatch(fetchAppointmentTypes(() => {}, notifyBugsnag));
	dispatch(fetchCategories(() => {}, notifyBugsnag));
	dispatch(fetchAcquisitionChannels(() => {}, notifyBugsnag));
	dispatch(fetchLeadTypes(() => {}, notifyBugsnag));
	dispatch(fetchBrands(() => {}, notifyBugsnag));
	dispatch(fetchTags(() => {}, notifyBugsnag));
	dispatch(fetchJobTags(() => {}, notifyBugsnag));
	dispatch(fetchOrganization(() => {}, notifyBugsnag));
	dispatch(fetchAssetCategories(() => {}, notifyBugsnag));
	dispatch(fetchTasks(() => {}, notifyBugsnag));
	dispatch(fetchTextMessages(() => {}, notifyBugsnag));
	dispatch(fetchJobAppointmentTypes(() => {}, notifyBugsnag));
	dispatch(fetchJobTypes(() => {}, notifyBugsnag));
	dispatch(fetchJobStatuses(() => {}, notifyBugsnag));
	dispatch(jobWarrantyRegistrationStatuses(() => {}, notifyBugsnag));
	dispatch(fetchLeadStatuses(() => {}, notifyBugsnag));
	dispatch(fetchLeadStatusConfig(() => {}, notifyBugsnag));
	dispatch(fetchMerchants(() => {}, notifyBugsnag));
	dispatch(fetchJobCustomFields(() => {}, notifyBugsnag));
	dispatch(fetchLeadCustomFields(() => {}, notifyBugsnag));
	dispatch(fetchPartnerBrandCustomFields(() => {}, notifyBugsnag));
	dispatch(fetchFinanceProducts(() => {}, notifyBugsnag));
}
